import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { CreatedPerson, Role } from "../../shared/types";
import { Dialog, Empty, Field } from "../components/ui";
import { post } from "../lib/api";
import { Check, LoadState, nameMap, orNull, PageHeader, toApiError, usePeople, useSites } from "./common";

export const ROLE_LABEL: Record<Role, string> = { admin: "Admin", cleaner: "Cleaner" };

function InviteDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  /** `askedToInvite`: the "email them an invite" box was ticked. */
  onSaved: (p: CreatedPerson, askedToInvite: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("cleaner");
  const [phone, setPhone] = useState("");
  const [qbName, setQbName] = useState("");
  const [qbEntity, setQbEntity] = useState<"employee" | "supplier">("employee");
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const p = await post<CreatedPerson>("/admin/people", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        role,
        phone: orNull(phone),
        qbName: orNull(qbName),
        qbEntity,
        sendInvite,
      });
      onSaved(p, sendInvite);
    } catch (err) {
      setError(toApiError(err).message);
      setBusy(false);
    }
  }

  return (
    <Dialog title="Add a person" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />
        </Field>
        <Field label="Email" hint="They sign in with a code sent to this address.">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={200}
            autoComplete="off"
            required
          />
        </Field>
        <div className="grid cols-2">
          <Field label="Role">
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="cleaner">Cleaner</option>
              <option value="admin">Admin (can see and change everything)</option>
            </select>
          </Field>
          <Field label="Phone (optional)">
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={30} />
          </Field>
        </div>
        <div className="grid cols-2">
          <Field label="QuickBooks name" hint="Exactly as in QuickBooks. Leave empty to use their name.">
            <input value={qbName} onChange={(e) => setQbName(e.target.value)} maxLength={200} />
          </Field>
          <Field label="In QuickBooks they are">
            <select value={qbEntity} onChange={(e) => setQbEntity(e.target.value as "employee" | "supplier")}>
              <option value="employee">An employee</option>
              <option value="supplier">A supplier (self-employed)</option>
            </select>
          </Field>
        </div>
        <Check
          label="Email them an invite with a link to the app"
          checked={sendInvite}
          onChange={setSendInvite}
        />
        {error && <div className="banner danger">{error}</div>}
        <div className="row">
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Saving…" : sendInvite ? "Add and send invite" : "Add person"}
          </button>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function PeoplePage() {
  const people = usePeople();
  const sites = useSites();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const siteNames = nameMap(sites.data);

  return (
    <div className="page">
      <PageHeader
        title="Cleaners"
        sub="Everyone who can sign in, including admins."
        actions={
          <button className="btn primary" onClick={() => setAdding(true)}>
            Add person
          </button>
        }
      />
      <LoadState state={people}>
        {(all) => {
          const inactive = all.filter((p) => !p.active).length;
          const list = showInactive ? all : all.filter((p) => p.active);
          return (
            <>
              {inactive > 0 && (
                <div className="row" style={{ justifyContent: "flex-end", marginBottom: 8 }}>
                  <Check
                    label={`Show inactive (${inactive})`}
                    checked={showInactive}
                    onChange={setShowInactive}
                  />
                </div>
              )}
              {list.length ? (
                <div className="table-wrap">
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Phone</th>
                        <th>QuickBooks name</th>
                        <th>Sites</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((p) => (
                        <tr key={p.id} className={p.active ? "" : "muted"}>
                          <td>
                            <Link to={`/admin/people/${p.id}`}>{p.name}</Link>
                            <div className="muted small">{p.email}</div>
                          </td>
                          <td>{ROLE_LABEL[p.role]}</td>
                          <td className="nowrap">{p.phone ?? "—"}</td>
                          <td>
                            {p.qbName ?? <span className="badge warn">Not set</span>}
                            {p.qbEntity === "supplier" && <div className="muted small">Supplier</div>}
                          </td>
                          <td className="small">
                            {p.siteIds.length ? (
                              p.siteIds.map((id) => siteNames.get(id) ?? "Unknown site").join(", ")
                            ) : (
                              <span className="badge warn">No sites assigned</span>
                            )}
                          </td>
                          <td>
                            {p.active ? (
                              <span className="badge ok">Active</span>
                            ) : (
                              <span className="badge">Deactivated</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="card">
                  <Empty>No one here yet. Add your first cleaner.</Empty>
                </div>
              )}
            </>
          );
        }}
      </LoadState>
      {adding && (
        <InviteDialog
          onClose={() => setAdding(false)}
          onSaved={(p, askedToInvite) =>
            navigate(`/admin/people/${p.id}`, {
              state: !askedToInvite
                ? { message: `Added ${p.name}.` }
                : p.inviteSent
                  ? { message: `Added ${p.name}. Invite emailed.` }
                  : {
                      message: `Person added, but the invite email couldn't be sent — they can still sign in with their email address (${p.email}).`,
                      tone: "warn",
                    },
            })
          }
        />
      )}
    </div>
  );
}
